import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "@/utils/axios";
import type {
  Correction,
  Revision,
  Filters,
  FormalityRevisionApiItem,
  FormalityRevisionApiResponse,
} from "./types";

export const useUpdateRevision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-revision"],
    mutationFn: async (data: {
      revision_id: string;
      revision_comment?: string;
      correction_comment?: string;
      group_id?: string;
      correction_fields: Array<{
        filled_field_id: string | null;
        field_id?: string;
        status: "APPROVED" | "DENIED" | "IN_PROCESS";
        comment?: string;
      }>;
    }) => {
      const response = await axios.put(
        `/admin/v1/formality-request/revision`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate form-detail queries to ensure fresh data on navigation
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
    },
  });
};

export const useLeaderApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["leader-approval"],
    mutationFn: async (data: {
      correction_id: string;
      approved: boolean;
      comments?: string;
    }) => {
      const response = await axios.post(
        `/admin/v1/formality-request/revision/corrections/leader-approval`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate form-detail and revisions queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
      queryClient.invalidateQueries({ queryKey: ["revisions"] });
    },
  });
};

const s = (v: string | number | null | undefined): string =>
  v == null ? "" : String(v);
const iso = (v: Date | string | number | null | undefined): string | null => {
  if (v == null) {
    return null;
  }
  if (v instanceof Date) {
    return v.toISOString();
  }
  return String(v);
};
const arr = <T>(v?: T[] | null): T[] => (Array.isArray(v) ? v : []);

function normalizeRevision(item: FormalityRevisionApiItem): Revision {
  const corrections: Correction[] = arr(item.corrections).map((c) => ({
    id: s(c.id),
    revision_id: s(c.revision_id ?? item.id),
    status: c.status,
    is_leader_approved: c.is_leader_approved ?? false,
    created_by_id: c.created_by_id == null ? null : s(c.created_by_id),
    created_by: c.created_by ?? null,
    creator: c.created_by ?? null,
    created_at: iso(c.created_at),
    updated_at: iso(c.updated_at),
    group: c.group
      ? {
          id: s(c.group.id),
          name: c.group.name,
          description: c.group.description ?? null,
        }
      : undefined,
    field_corrections: arr(c.field_corrections).map((fc) => ({
      id: s(fc.id),
      correction_id: s(fc.correction_id),
      filled_field_id: s(fc.filled_field_id),
      status: fc.status,
      comments: fc.comments ?? null,
      created_at: iso(fc.created_at),
      updated_at: iso(fc.updated_at),
    })),
    comments: arr(c.comments).map((m) => ({
      id: s(m.id),
      text: m.text ?? "",
      created_by: m.created_by == null ? null : s(m.created_by),
      created_at: iso(m.created_at),
      updated_at: iso(m.updated_at),
      creator: m.creator ?? null,
    })),
  }));

  const groups = arr(item.groups).map((g) => ({
    id: s(g.id),
    name: g.name,
    description: g.description ?? null,
  }));

  return {
    id: s(item.id),
    status: item?.applicant_form_assignment?.status || item.status,
    requires_leader_approval: item.requires_leader_approval ?? false,
    applicant_form_assignment_id:
      item.applicant_form_assignment_id == null
        ? null
        : s(item.applicant_form_assignment_id),
    applicant_form_assignment: item.applicant_form_assignment
      ? {
          id: s(item.applicant_form_assignment.id),
          status: item.applicant_form_assignment.status,
          applicant: item.applicant_form_assignment.applicant
            ? {
                id: s(item.applicant_form_assignment.applicant.id),
                first_name: item.applicant_form_assignment.applicant.first_name,
                last_name: item.applicant_form_assignment.applicant.last_name,
                email: item.applicant_form_assignment.applicant.email,
                document_number:
                  item.applicant_form_assignment.applicant.document_number,
              }
            : undefined,
        }
      : null,
    created_by: item.created_by == null ? null : s(item.created_by),
    created_at: iso(item.created_at),
    updated_at: iso(item.updated_at),
    corrections,
    denied_corrections: arr(item.denied_corrections).map((dc) => ({
      id: s(dc.id),
      revision_id: s(dc.revision_id ?? item.id),
      status: dc.status,
      is_leader_approved: dc.is_leader_approved ?? false,
      created_by_id: dc.created_by_id == null ? null : s(dc.created_by_id),
      created_by: dc.created_by ?? null,
      creator: dc.created_by ?? null,
      created_at: iso(dc.created_at),
      updated_at: iso(dc.updated_at),
      group: dc.group
        ? {
            id: s(dc.group.id),
            name: dc.group.name,
            description: dc.group.description ?? null,
          }
        : undefined,
      comments: arr(dc.comments).map((m) => ({
        id: s(m.id),
        text: m.text ?? "",
        created_by: m.created_by == null ? null : s(m.created_by),
        created_at: iso(m.created_at),
        updated_at: iso(m.updated_at),
        creator: m.creator ?? null,
      })),
    })),
    groups: groups.length > 0 ? groups : undefined,
  };
}

function adaptToRevisions(payload: FormalityRevisionApiResponse): Revision[] {
  const list: FormalityRevisionApiItem[] = Array.isArray(payload)
    ? payload
    : (payload?.data ?? []);
  return list.map(normalizeRevision);
}

// Query params
function buildSearchParams(params: {
  formality_request_id: string;
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}): URLSearchParams {
  const sp = new URLSearchParams();
  sp.append("filter[formality_request_id]", params.formality_request_id);

  // Filtros
  const entries = Object.entries(params.filters ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [key, raw] of entries) {
    if (raw == null) continue;
    const push = (v: string | number | boolean) =>
      sp.append(`filter[${key}]`, String(v));
    if (Array.isArray(raw)) {
      raw.forEach((v) => {
        if (v != null) push(v);
      });
    } else {
      push(raw);
    }
  }

  if (params.relationships?.length)
    sp.set("relationships", params.relationships.join(","));

  if (params.page != null) sp.set("page", String(params.page));
  if (params.per_page != null) sp.set("per_page", String(params.per_page));
  if (params.order_by != null) sp.set("order_by", String(params.order_by));
  if (params.order_direction != null)
    sp.set("order_direction", String(params.order_direction));

  return sp;
}

// API
export const getFormalityRequestRevision = async (params: {
  formality_request_id: string;
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}): Promise<Revision[]> => {
  const searchParams = buildSearchParams(params);
  const { data } = await axios.get(
    `/admin/v1/formality-request/revision?${searchParams.toString()}`,
  );
  return adaptToRevisions(data);
};

export const getFormalityRequestRevisionExpired = async (
  formality_request_id: string,
): Promise<Revision[]> => {
  const { data } = await axios.get(
    `/admin/v1/formality-request/${formality_request_id}/expired-revisions`,
  );
  return adaptToRevisions(data);
};

// Hook
export const useGetFormalityRequestRevision = (params: {
  formality_request_id: string;
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}) => {
  const key = [
    "formality-request-revision",
    params.formality_request_id,
    params.relationships
      ?.slice()
      .sort((a, b) => a.localeCompare(b))
      .join(",") ?? "",
    JSON.stringify(
      Object.fromEntries(
        Object.entries(params.filters ?? {}).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      ),
    ),
    params.page ?? null,
    params.per_page ?? null,
    params.order_by ?? null,
    params.order_direction ?? null,
  ];

  return useQuery<Revision[], unknown, Revision[]>({
    queryKey: key,
    queryFn: () => getFormalityRequestRevision(params),
    enabled: !!params.formality_request_id,
  });
};

// New endpoint: /admin/v1/revisions
export const getRevisions = async (params: {
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
}): Promise<Revision[]> => {
  const sp = new URLSearchParams();

  // Filtros
  const entries = Object.entries(params.filters ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [key, raw] of entries) {
    if (raw == null) continue;
    const push = (v: string | number | boolean) =>
      sp.append(`filter[${key}]`, String(v));
    if (Array.isArray(raw)) {
      raw.forEach((v) => {
        if (v != null) push(v);
      });
    } else {
      push(raw);
    }
  }

  if (params.relationships?.length)
    sp.set("relationships", params.relationships.join(","));

  if (params.page != null) sp.set("page", String(params.page));
  if (params.per_page != null) sp.set("per_page", String(params.per_page));
  if (params.order_by != null) sp.set("order_by", String(params.order_by));
  if (params.order_direction != null)
    sp.set("order_direction", String(params.order_direction));

  const { data } = await axios.get(`/admin/v1/revisions?${sp.toString()}`);
  return adaptToRevisions(data);
};

export const useGetRevisions = (params: {
  relationships?: string[];
  filters?: Filters;
  page?: number;
  per_page?: number;
  order_by?: string;
  order_direction?: string;
  enabled?: boolean;
}) => {
  const { enabled = true, ...restParams } = params;
  const key = [
    "revisions",
    restParams.relationships
      ?.slice()
      .sort((a, b) => a.localeCompare(b))
      .join(",") ?? "",
    JSON.stringify(
      Object.fromEntries(
        Object.entries(restParams.filters ?? {}).sort(([a], [b]) =>
          a.localeCompare(b),
        ),
      ),
    ),
    restParams.page ?? null,
    restParams.per_page ?? null,
    restParams.order_by ?? null,
    restParams.order_direction ?? null,
  ];

  return useQuery<Revision[], unknown, Revision[]>({
    queryKey: key,
    queryFn: () => getRevisions(restParams),
    enabled,
  });
};
